import type { StatusTone } from '@orbind/tokens'
import { Badge } from '@orbind/ui'
import { useT } from '../lib/i18n.tsx'

/**
 * Badges derivados de catalogo.
 *
 * O rotulo vem sempre da chave de traducao do catalogo, nunca do `code`: o code
 * e tecnico (IN_PROGRESS) e o usuario ve o texto traduzido (Em andamento).
 * Como catalogos sao cadastraveis, nao ha switch sobre valores fixos.
 */

type CatalogRef = { code: string; tone: string; nameTranslationKey?: string | null; icon?: string | null }

const STATUS_ICON: Record<string, string> = {
  BLOCKED: 'fas fa-ban',
  DONE: 'fas fa-check',
  CANCELLED: 'fas fa-times',
  IN_REVIEW: 'fas fa-search',
  IN_PROGRESS: 'fas fa-play',
}

export function StatusBadge({ status }: { status: CatalogRef }) {
  const t = useT()
  const key = status.nameTranslationKey ?? `work_item_status.${status.code.toLowerCase()}.name`
  const label = t(key)
  return (
    <Badge tone={status.tone as StatusTone} icon={STATUS_ICON[status.code]}>
      {label === key ? status.code : label}
    </Badge>
  )
}

export function TypeBadge({ type }: { type: CatalogRef }) {
  const t = useT()
  const key = type.nameTranslationKey ?? `work_item_type.${type.code.toLowerCase()}.name`
  const label = t(key)
  return (
    <Badge tone={type.tone as StatusTone} icon={type.icon ?? undefined}>
      {label === key ? type.code : label}
    </Badge>
  )
}

export function PriorityBadge({ priority }: { priority: CatalogRef }) {
  const t = useT()
  const key = priority.nameTranslationKey ?? `priority.${priority.code.toLowerCase()}.name`
  const label = t(key)
  return <Badge tone={priority.tone as StatusTone}>{label === key ? priority.code : label}</Badge>
}

const DAY_MS = 86_400_000

/**
 * Rotulo de prazo.
 *
 * Atraso vem com icone E texto, nunca so com cor (spec 6). Itens ja concluidos
 * nao mostram atraso — o prazo deixou de exigir acao.
 */
export function dueLabel(dueAt: string | Date | null, isTerminal: boolean) {
  if (!dueAt || isTerminal) return null
  const due = typeof dueAt === 'string' ? new Date(dueAt) : dueAt
  const days = Math.ceil((due.getTime() - Date.now()) / DAY_MS)

  if (days < 0) {
    const late = Math.abs(days)
    return (
      <Badge tone="danger" icon="fas fa-exclamation-triangle">
        {late === 1 ? 'Atrasado 1 dia' : `Atrasado ${late} dias`}
      </Badge>
    )
  }
  if (days === 0) {
    return (
      <Badge tone="warning" icon="fas fa-clock">
        Vence hoje
      </Badge>
    )
  }
  if (days <= 3) {
    return (
      <Badge tone="warning" icon="fas fa-clock">
        {`Vence em ${days} dias`}
      </Badge>
    )
  }
  return (
    <Badge tone="neutral">
      {new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' }).format(due)}
    </Badge>
  )
}

/** Estado de SLA da demanda, com icone e texto explicito (spec 12.1 e 21). */
export function SlaBadge({
  sla,
  dueAt,
  pausedAt,
}: {
  sla: 'none' | 'on-track' | 'at-risk' | 'breached'
  dueAt: string | null
  /** Preenchido quando a demanda aguarda o solicitante: o relogio parou. */
  pausedAt?: string | null
}) {
  if (sla === 'none') return null
  // Pausado vence a leitura do estado: mostrar "dentro do prazo" com o relogio
  // parado esconderia a informacao que importa para quem atende.
  if (pausedAt) {
    return (
      <Badge tone="neutral" icon="fas fa-pause">
        SLA pausado
      </Badge>
    )
  }
  if (sla === 'breached') {
    const days = dueAt ? Math.floor((Date.now() - new Date(dueAt).getTime()) / DAY_MS) : 0
    return (
      <Badge tone="danger" icon="fas fa-exclamation-triangle">
        {days > 0 ? `SLA vencido há ${days} dias` : 'SLA vencido'}
      </Badge>
    )
  }
  if (sla === 'at-risk') {
    return (
      <Badge tone="warning" icon="fas fa-clock">
        SLA em risco
      </Badge>
    )
  }
  return (
    <Badge tone="success" icon="fas fa-check">
      Dentro do SLA
    </Badge>
  )
}
